import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import {
    initTopUp,
    buySubscription,
    transferToOffice,
    getOfficeBillingSummary,
} from '../../actions/billing.action';
import { getOffices } from '../../actions/officeAction';
import { getOfficeMembers } from '../../actions/officeMember.actions';
import { closeAccessGate, clearAccessGate } from '../../actions/accessGate.actions';
import SubscriptionPlansModal from './SubscriptionPlansModal';
import {
    canManageOfficeFinanceFront,
    getOfficeFinanceAccessHint,
    getOfficeRelation,
} from '../../containers/AdminOffice/rbac';


const emptySubscription = {
    type: 'none',
    plan: 'none',
    validUntil: null,
    autoRenew: false,
    status: 'inactive',
};

const isActiveSubscription = (subscription = {}) => {
    if (!subscription?.validUntil) return false;
    if (subscription.type === 'none') return false;
    if (subscription.status === 'inactive') return false;
    if (subscription.status === 'past_due') return false;

    return new Date(subscription.validUntil) > new Date();
};

const formatMoney = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;

const PLATFORM_ADMIN_ROLES = ['admin', 'superAdmin'];

const getEntityId = (value) => String(value?._id || value?.id || value || '');

const normalizeRole = (value) => String(value || '').trim().toLowerCase();

const hasOfficeOwnerTransferAccess = ({ user = {}, office = {}, currentMember = null, relation = {} }) => {
    const userId = getEntityId(user);
    const createdById = getEntityId(office?.createdBy);
    const memberRole = normalizeRole(currentMember?.role);

    if (!userId) return false;
    if (PLATFORM_ADMIN_ROLES.includes(user?.role)) return true;
    if (createdById && createdById === userId) return true;
    if (memberRole === 'owner') return true;
    if (relation?.isOfficeCreator || relation?.isOfficeOwner || relation?.isOwner || relation?.ownerLike) return true;

    return false;
};

const hasOfficeFinancePaymentAccess = ({ user = {}, currentMember = null, permissions = {}, canTransferOfficeFunds = false }) => {
    const memberRole = normalizeRole(currentMember?.role);

    if (PLATFORM_ADMIN_ROLES.includes(user?.role)) return true;
    if (canTransferOfficeFunds) return true;
    if (memberRole === 'admin') return true;
    if (permissions?.finance === true) return true;

    return false;
};

const getGateMeta = (gate = {}) => {
    const code = gate.code || 'FEATURE_ACCESS_REQUIRED';

    const meta = {
        title: 'Нужен платный доступ',
        featureLabel: 'Платная функция',
        scopeLabel: 'Сайт',
        explain: 'Доступ к этой функции ограничен тарифом или разовой оплатой.',
        subscriptionButton: 'Купить подписку',
        canBuyOfficeSubscription: true,
        canBuyUserSubscription: false,
        canBuyOneTimeScene: false,
        requiresOfficeFinance: true,
    };

    if (['OFFICE_SUBSCRIPTION_REQUIRED', 'OFFICE_3D_SUBSCRIPTION_REQUIRED', 'OFFICE_GUEST_SCENE_SUBSCRIPTION_REQUIRED'].includes(code)) {
        return {
            ...meta,
            title: 'Подписка офиса закончилась',
            featureLabel: '3D-калькулятор офиса',
            scopeLabel: 'Офис',
            explain: 'Для загрузки 3D-моделей нужна активная подписка офиса. Личный баланс сам по себе не включает офисную подписку.',
            subscriptionButton: 'Продлить подписку офиса',
            canBuyOfficeSubscription: true,
            canBuyUserSubscription: false,
            canBuyOneTimeScene: false,
            requiresOfficeFinance: true,
        };
    }

    if (code === 'WAREHOUSE_SUBSCRIPTION_REQUIRED') {
        return {
            ...meta,
            title: 'Склад закрыт по подписке',
            featureLabel: 'Склад',
            scopeLabel: 'Офис',
            explain: 'Работа со складом будет доступна после активации офисного тарифа.',
            subscriptionButton: 'Продлить подписку офиса',
            canBuyOfficeSubscription: true,
            requiresOfficeFinance: true,
        };
    }

    if (code === 'PRINT_FORM_SUBSCRIPTION_REQUIRED') {
        return {
            ...meta,
            title: 'Печатные формы закрыты по подписке',
            featureLabel: 'Печать формы',
            scopeLabel: 'Офис',
            explain: 'Для печатных форм нужен активный офисный тариф.',
            subscriptionButton: 'Продлить подписку офиса',
            canBuyOfficeSubscription: true,
            requiresOfficeFinance: true,
        };
    }

    if (code === 'OFFICE_SUBSCRIPTION_FUNDS_REQUIRED' || code === 'OFFICE_BALANCE_REQUIRED') {
        return {
            ...meta,
            title: 'Недостаточно средств для подписки',
            featureLabel: 'Подписка офиса',
            scopeLabel: 'Офис',
            explain: 'Пополните баланс офиса или переведите средства на офис, затем повторите оплату подписки.',
            subscriptionButton: 'Открыть подписку офиса',
            canBuyOfficeSubscription: true,
            canBuyOneTimeScene: false,
            requiresOfficeFinance: true,
        };
    }

    if (['SCENE_PAYMENT_REQUIRED', 'SCENE_ACCESS_REQUIRED', 'SCENE_EDIT_ACCESS_EXPIRED', 'SCENE_BILLING_LIMIT_EXCEEDED'].includes(code)) {
        return {
            ...meta,
            title: code === 'SCENE_EDIT_ACCESS_EXPIRED' ? 'Редактирование legacy-сцены истекло' : 'Нужна активная подписка офиса',
            featureLabel: '3D-сцена',
            scopeLabel: 'Сцена',
            explain: 'Новые сцены создаются по правилам Billing V2: сначала включённые сцены периода, затем пакетные сцены, затем списание с баланса офиса.',
            subscriptionButton: 'Открыть подписку офиса',
            canBuyOfficeSubscription: true,
            canBuyOneTimeScene: false,
            requiresOfficeFinance: true,
        };
    }

    if (code === 'USER_PRO_REQUIRED') {
        return {
            ...meta,
            title: 'Нужен офис',
            featureLabel: 'Офисный доступ',
            scopeLabel: 'Пользователь',
            explain: 'Личная подписка больше не используется. Создайте или выберите офис и активируйте его подписку.',
            subscriptionButton: 'Открыть офисный биллинг',
            canBuyOfficeSubscription: true,
            canBuyUserSubscription: false,
            canBuyOneTimeScene: false,
            requiresOfficeFinance: false,
        };
    }

    return meta;
};

const AccessGateHost = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const gate = useSelector((state) => state.accessGate || {}, shallowEqual);
    const { user = {} } = useSelector((state) => state.auth || {}, shallowEqual);
    const {
        offices = [],
        currentOfficeId = null,
    } = useSelector((state) => state.office || {}, shallowEqual);
    const {
        currentMemberByOffice = {},
        permissionsByOffice = {},
    } = useSelector((state) => state.officeMember || {}, shallowEqual);
    const {
        topupLoading,
        transferLoading,
        subscriptionLoading,
    } = useSelector((state) => state.billing || {}, shallowEqual);

    const [topUpAmount, setTopUpAmount] = useState('');
    const [transferAmount, setTransferAmount] = useState('');
    const [mockTopUp, setMockTopUp] = useState(process.env.NODE_ENV !== 'production');
    const [plansOpen, setPlansOpen] = useState(false);
    const [plansTarget, setPlansTarget] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [inlineError, setInlineError] = useState('');
    const [billingSummary, setBillingSummary] = useState(null);

    const cleanOfficeId = gate.officeId || gate.payload?.officeId || (
        currentOfficeId && currentOfficeId !== 'all' ? currentOfficeId : null
    );

    const focusedOffice = useMemo(() => {
        if (!cleanOfficeId) return null;

        return (offices || []).find((office) => (
            String(office?._id || office?.id) === String(cleanOfficeId)
        )) || null;
    }, [offices, cleanOfficeId]);

    const currentMember = cleanOfficeId ? (currentMemberByOffice?.[cleanOfficeId] || null) : null;
    const permissions = cleanOfficeId
        ? (permissionsByOffice?.[cleanOfficeId] || currentMember?.permissions || {})
        : {};

    const relation = useMemo(() => {
        return getOfficeRelation({
            user,
            office: focusedOffice,
            currentMember,
            permissions,
        });
    }, [user, focusedOffice, currentMember, permissions]);

    const canManageFinanceByRbac = useMemo(() => {
        if (!cleanOfficeId) return false;

        return canManageOfficeFinanceFront({
            user,
            office: focusedOffice,
            currentMember,
            permissions,
        });
    }, [cleanOfficeId, user, focusedOffice, currentMember, permissions]);

    const canTransferOfficeFunds = useMemo(() => {
        if (!cleanOfficeId) return false;

        return hasOfficeOwnerTransferAccess({
            user,
            office: focusedOffice,
            currentMember,
            relation,
        });
    }, [cleanOfficeId, user, focusedOffice, currentMember, relation]);

    // Оплатить подписку/разовую офисную функцию может любой сотрудник,
    // который уже имеет доступ к офису. Сервер повторно проверяет membership.
    const canBuyOfficePaidAccess = !!(cleanOfficeId && focusedOffice);

    const financeAccessHint = useMemo(() => {
        return getOfficeFinanceAccessHint({
            canManageFinance: canBuyOfficePaidAccess || canManageFinanceByRbac,
            relation,
        });
    }, [canBuyOfficePaidAccess, canManageFinanceByRbac, relation]);

    const meta = useMemo(() => getGateMeta(gate), [gate]);
    const officeSubscription = {
        ...emptySubscription,
        ...(focusedOffice?.subscription || {}),
    };
    const officeHasSubscription = isActiveSubscription(officeSubscription);

    const officeBalance = Number(billingSummary?.balance ?? focusedOffice?.balance ?? 0);
    const userBalance = Number(user?.balance || 0);

    const needsOfficeFinance = !!cleanOfficeId && !!meta.requiresOfficeFinance && !meta.canBuyUserSubscription;
    const paymentsLockedByRole = needsOfficeFinance && !canBuyOfficePaidAccess;
    const transferLockedByRole = needsOfficeFinance && !canTransferOfficeFunds;
    const roleLockedText = paymentsLockedByRole
        ? 'Не удалось подтвердить доступ к офису. Обновите страницу и попробуйте снова.'
        : '';
    const transferLockedText = transferLockedByRole
        ? 'Вы можете пополнить личный баланс, но перевести деньги на баланс офиса может только владелец офиса или пользователь, который создал этот офис. Обратитесь к владельцу офиса.'
        : '';
    const visibleRoleLockText = roleLockedText || transferLockedText;

    useEffect(() => {
        if (!gate.open || !cleanOfficeId) return;

        dispatch(getOfficeMembers(cleanOfficeId));
    }, [dispatch, gate.open, cleanOfficeId]);

    useEffect(() => {
        if (!gate.open || !cleanOfficeId) {
            setBillingSummary(null);
            return undefined;
        }

        let cancelled = false;
        setBillingSummary(focusedOffice?.billingSummary || null);

        Promise.resolve(dispatch(getOfficeBillingSummary(cleanOfficeId)))
            .then((summary) => {
                if (!cancelled && summary) setBillingSummary(summary);
            })
            .catch(() => null);

        return () => {
            cancelled = true;
        };
    }, [dispatch, gate.open, cleanOfficeId, focusedOffice?.billingSummary]);

    useEffect(() => {
        if (!gate.open) return;

        setInlineError('');
    }, [gate.open, gate.code, cleanOfficeId]);

    if (!gate.open || typeof document === 'undefined') {
        return null;
    }

    const targetTitle = focusedOffice?.name || gate.officeName || 'Офис';
    const scenePrice = Math.max(0, Number(billingSummary?.scenePrice ?? billingSummary?.settings?.extraScenePrice ?? 0));
    const includedTotal = Math.max(0, Number(billingSummary?.includedScenes?.total ?? billingSummary?.settings?.includedScenes ?? 0));
    const packageCredits = Math.max(0, Number(billingSummary?.packageCredits || 0));
    const monthlyPrice = Math.max(0, Number(billingSummary?.monthlyPrice ?? billingSummary?.settings?.monthlyPrice ?? 0));
    const periodDays = Math.max(0, Number(billingSummary?.periodDays ?? billingSummary?.settings?.periodDays ?? 0));
    const isSceneGate = ['SCENE_PAYMENT_REQUIRED', 'SCENE_ACCESS_REQUIRED', 'SCENE_EDIT_ACCESS_EXPIRED', 'SCENE_BILLING_LIMIT_EXCEEDED'].includes(gate.code);
    const dynamicSceneExplain = `В период включено ${includedTotal} сцен, пакетных осталось ${packageCredits}. После них новая сцена стоит ${formatMoney(scenePrice)}.`;
    const message = gate.message || (isSceneGate && billingSummary ? dynamicSceneExplain : meta.explain);

    const denyByRole = (text = '') => {
        setInlineError(text || roleLockedText || transferLockedText || financeAccessHint);
    };

    const openOfficePlans = () => {
        if (!cleanOfficeId) {
            swal('Офис не выбран', 'Сначала выберите офис, для которого нужно продлить подписку.', 'warning');
            return;
        }

        if (paymentsLockedByRole) {
            denyByRole(roleLockedText);
            return;
        }

        setPlansTarget({
            targetModel: 'Office',
            targetId: cleanOfficeId,
            title: targetTitle,
            balance: officeBalance,
            subscription: officeSubscription,
        });
        setPlansOpen(true);
    };

    const handleBuySubscription = async (plan) => {
        const target = plansTarget;

        if (!target?.targetId) {
            swal('Ошибка', 'Не найден получатель подписки.', 'error');
            return false;
        }

        if (target.targetModel === 'Office' && paymentsLockedByRole) {
            denyByRole();
            return false;
        }

        const success = await dispatch(buySubscription({
            targetModel: target.targetModel,
            targetId: target.targetId,
            plan,
        }));

        if (success) {
            setPlansOpen(false);
            setPlansTarget(null);
            dispatch(getOffices());
            dispatch(closeAccessGate());
        }

        return success;
    };

    const handleTopUp = async () => {
        const amount = Number(topUpAmount);

        if (!amount || amount <= 0) {
            swal('Ошибка', 'Введите сумму пополнения.', 'warning');
            return;
        }

        await dispatch(initTopUp({
            targetModel: cleanOfficeId ? 'Office' : 'User',
            targetId: cleanOfficeId || user?._id,
            amount,
            isMock: mockTopUp,
        }));

        setTopUpAmount('');
    };

    const handleTransferToOffice = async () => {
        const amount = Number(transferAmount);

        if (!cleanOfficeId) {
            swal('Офис не выбран', 'Нужно выбрать офис для перевода средств.', 'warning');
            return;
        }

        if (transferLockedByRole) {
            denyByRole(transferLockedText);
            return;
        }

        if (!amount || amount <= 0) {
            swal('Ошибка', 'Введите сумму перевода.', 'warning');
            return;
        }

        const success = await dispatch(transferToOffice({
            officeId: cleanOfficeId,
            amount,
            description: 'Перевод средств на офис для продления платного доступа',
        }));

        if (success) {
            setTransferAmount('');
        }
    };

    const handleOpenBilling = () => {
        dispatch(closeAccessGate());

        if (cleanOfficeId) {
            navigate(`/adminLayout?tab=billing&office=${cleanOfficeId}`);
            return;
        }

        navigate('/personalInformation');
    };

    const handleRefreshAccess = async () => {
        setRefreshing(true);

        try {
            const data = await dispatch(getOffices());
            const nextOffice = (data?.offices || []).find((office) => (
                String(office?._id || office?.id) === String(cleanOfficeId)
            ));

            if (nextOffice && isActiveSubscription(nextOffice.subscription || {})) {
                dispatch(closeAccessGate());
                swal('Доступ восстановлен', 'Подписка офиса активна.', 'success');
                return;
            }

            swal('Доступ ещё закрыт', 'Подписка офиса пока не активирована.', 'warning');
        } finally {
            setRefreshing(false);
        }
    };

    const handleClose = () => {
        dispatch(closeAccessGate());
    };

    const handleClear = () => {
        dispatch(clearAccessGate());
    };

    const modal = (
        <div className="access-gate" role="dialog" aria-modal="true">
            <div className="access-gate__backdrop" />

            <div className="access-gate__modal">
                <div className="access-gate__header">
                    <div>
                        <div className="access-gate__kicker">{meta.scopeLabel} / {meta.featureLabel}</div>
                        <h3>{gate.title || meta.title}</h3>
                        <p>{message}</p>
                    </div>

                    <button
                        type="button"
                        className="access-gate__close"
                        onClick={handleClose}
                        title="Закрыть"
                    >
                        ×
                    </button>
                </div>

                <div className="access-gate__notice">
                    <b>Важно:</b> личный баланс и баланс офиса — разные кошельки. Подписка, пакеты сцен и платные сцены списываются с баланса офиса.
                    {transferLockedByRole ? ' Для вашей роли перевод на офис заблокирован — это должен сделать владелец офиса.' : ''}
                </div>

                {(paymentsLockedByRole || transferLockedByRole) ? (
                    <div className="access-gate__role-lock">
                        <b>{paymentsLockedByRole ? 'Оплату должен выполнить владелец или ответственный сотрудник' : 'Перевод на офис доступен только владельцу'}</b>
                        <span>{visibleRoleLockText}</span>
                    </div>
                ) : null}

                {inlineError ? (
                    <div className="access-gate__inline-error">
                        {inlineError}
                    </div>
                ) : null}

                <div className="access-gate__grid">
                    <div className="access-gate__card">
                        <span>Личный баланс</span>
                        <strong>{formatMoney(userBalance)}</strong>
                        <small>Личной подписки нет</small>
                    </div>

                    <div className="access-gate__card">
                        <span>Офис</span>
                        <strong>{targetTitle}</strong>
                        <small>{cleanOfficeId || 'не выбран'}</small>
                    </div>

                    <div className="access-gate__card">
                        <span>Баланс офиса</span>
                        <strong>{formatMoney(officeBalance)}</strong>
                        <small>
                            {officeHasSubscription
                                ? `Активна${billingSummary ? ` · доступно с кредитом ${formatMoney(billingSummary.availableWithCredit || 0)}` : ''}`
                                : 'Подписка офиса неактивна'}
                        </small>
                    </div>
                </div>

                <div className="access-gate__section">
                    <div>
                        <h4>Подписка офиса</h4>
                        <p>
                            {billingSummary
                                ? `Единый тариф: ${formatMoney(monthlyPrice)} / ${periodDays || '—'} дней. Включено ${includedTotal} сцен, далее пакеты и ${formatMoney(scenePrice)} за сцену.`
                                : 'Актуальные параметры тарифа загружаются с сервера.'}
                            {paymentsLockedByRole ? ` ${financeAccessHint}` : ''}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="access-gate__primary"
                        onClick={openOfficePlans}
                        disabled={subscriptionLoading || (meta.canBuyOfficeSubscription && paymentsLockedByRole)}
                        title={paymentsLockedByRole ? roleLockedText : ''}
                    >
                        {subscriptionLoading
                            ? 'Обработка...'
                            : paymentsLockedByRole
                                ? 'Нет доступа к офису'
                                : meta.subscriptionButton}
                    </button>
                </div>

                <div className="access-gate__section access-gate__section--split">
                    <div>
                        <h4>Пополнить и перевести</h4>
                        <p>
                            Любой сотрудник может пополнить баланс офиса картой и оплатить подписку. Перевод с личного внутреннего баланса остаётся отдельной операцией владельца.
                            {transferLockedByRole ? ' Для текущей роли перевод на офис заблокирован: перевод может выполнить только владелец офиса.' : ''}
                        </p>
                    </div>

                    <div className="access-gate__forms">
                        <div className="access-gate__inline-form">
                            <input
                                type="number"
                                value={topUpAmount}
                                placeholder={cleanOfficeId ? "Сумма пополнения офиса" : "Сумма личного пополнения"}
                                onChange={(event) => setTopUpAmount(event.target.value)}
                            />

                            {process.env.NODE_ENV !== 'production' ? (
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={mockTopUp}
                                        onChange={(event) => setMockTopUp(event.target.checked)}
                                    />
                                    test
                                </label>
                            ) : null}

                            <button
                                type="button"
                                onClick={handleTopUp}
                                disabled={topupLoading || !topUpAmount}
                            >
                                {topupLoading ? 'Пополнение...' : cleanOfficeId ? 'Пополнить офис картой' : 'Пополнить личный'}
                            </button>
                        </div>

                        <div className="access-gate__inline-form">
                            <input
                                type="number"
                                value={transferAmount}
                                placeholder={transferLockedByRole ? 'Перевод доступен только владельцу' : 'Сумма на офис'}
                                onChange={(event) => setTransferAmount(event.target.value)}
                                disabled={transferLockedByRole}
                            />

                            <button
                                type="button"
                                onClick={handleTransferToOffice}
                                disabled={transferLoading || !transferAmount || !cleanOfficeId || transferLockedByRole}
                                title={transferLockedByRole ? transferLockedText : ''}
                            >
                                {transferLoading
                                    ? 'Перевод...'
                                    : transferLockedByRole
                                        ? 'Нет прав на перевод'
                                        : 'Перевести на офис'}
                            </button>
                        </div>
                    </div>
                </div>


                <div className="access-gate__footer">
                    <button type="button" onClick={handleOpenBilling}>
                        Открыть биллинг
                    </button>

                    <button
                        type="button"
                        onClick={handleRefreshAccess}
                        disabled={refreshing}
                    >
                        {refreshing ? 'Проверка...' : 'Проверить доступ'}
                    </button>

                    <button type="button" onClick={handleClear}>
                        Скрыть до следующей ошибки
                    </button>
                </div>

                <SubscriptionPlansModal
                    visible={plansOpen}
                    onClose={() => {
                        setPlansOpen(false);
                        setPlansTarget(null);
                    }}
                    onBuy={handleBuySubscription}
                    loading={subscriptionLoading}
                    balance={plansTarget?.balance || 0}
                    targetModel={plansTarget?.targetModel || 'Office'}
                    targetTitle={plansTarget?.title || ''}
                    currentSubscription={plansTarget?.subscription || emptySubscription}
                />
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

export default AccessGateHost;
